import { headerKeyOf } from './donation-import.js';
import { readStatementGrid, type StatementFileKind } from './statement-file.js';
import type { InflateRaw } from './zip-read.js';

/**
 * A bulk add-and-update template — docs/decisions/0125. Pure: the template the
 * worker writes and the upload the worker reads are both decided here, so the
 * column a template offers is the column an upload is read by.
 */

export const BULK_CHANGE_KINDS = ['user', 'institution', 'donation'] as const;
export type BulkChangeKind = (typeof BULK_CHANGE_KINDS)[number];

/** The raw file's ceiling, the same 5 MiB the upload is presigned for. */
export const BULK_CHANGE_MAX_BYTES = 5 * 1024 * 1024;

/**
 * The most data rows one bulk change may hold. Lower than a donation import's
 * 2,000 because a created user is invited by a mail the commit awaits, one at
 * a time, within the mailer's own deadline (docs/decisions/0105).
 */
export const BULK_CHANGE_MAX_ROWS = 1_000;

/** The containers an upload may be; a CSV is read with its delimiter detected. */
export const BULK_CHANGE_FILE_KINDS = [
  'xlsx',
  'xls',
  'csv',
] as const satisfies readonly StatementFileKind[];
export type BulkChangeFileKind = (typeof BULK_CHANGE_FILE_KINDS)[number];

export function isBulkChangeFileKind(kind: string): kind is BulkChangeFileKind {
  return (BULK_CHANGE_FILE_KINDS as readonly string[]).includes(kind);
}

/** Which layout of the template a stamp names; a stamp of any other is not read. */
export const BULK_CHANGE_TEMPLATE_VERSION = 1;

/**
 * The layout a donation template is drawn in: one campaign and the published
 * form version its columns were laid out from — docs/decisions/0134.
 */
export const BULK_DONATION_TEMPLATE_VERSION = 2;

const STAMP_PREFIX = 'btsis:bulk-change';
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const STAMP_PATTERN = new RegExp(`^${STAMP_PREFIX}:(\\d{1,4}):(user|institution|donation)$`, 'i');
const DONATION_STAMP_PATTERN = new RegExp(
  `^${STAMP_PREFIX}:(\\d{1,4}):donation:(${UUID}):(\\d{1,9})$`,
  'i',
);

/** What A1 says: which kind of record the file was drawn for. */
export function bulkChangeStamp(kind: BulkChangeKind): string {
  return `${STAMP_PREFIX}:${BULK_CHANGE_TEMPLATE_VERSION}:${kind}`;
}

export type BulkDonationStamp = { campaignId: string; formVersion: number };

/** A donation template's A1: the kind, and the campaign and form version it was laid out from. */
export function bulkDonationStamp(stamp: BulkDonationStamp): string {
  return `${STAMP_PREFIX}:${BULK_DONATION_TEMPLATE_VERSION}:donation:${stamp.campaignId}:${stamp.formVersion}`;
}

export type BulkChangeStamp = { kind: BulkChangeKind; donation: BulkDonationStamp | null };

/**
 * A1 back out of a cell. A donation stamp of the old layout — no campaign —
 * is still a donation stamp, with `donation: null`, so the reader can tell the
 * person to download the template again rather than call the file unknown.
 */
export function readBulkChangeStampDetail(cell: string | null | undefined): BulkChangeStamp | null {
  const text = (cell ?? '').trim();
  const donation = DONATION_STAMP_PATTERN.exec(text);
  if (donation) {
    if (Number(donation[1]) !== BULK_DONATION_TEMPLATE_VERSION) return null;
    return {
      kind: 'donation',
      donation: {
        campaignId: (donation[2] as string).toLowerCase(),
        formVersion: Number(donation[3]),
      },
    };
  }
  const match = STAMP_PATTERN.exec(text);
  if (!match) return null;
  if (Number(match[1]) !== BULK_CHANGE_TEMPLATE_VERSION) return null;
  return { kind: (match[2] as string).toLowerCase() as BulkChangeKind, donation: null };
}

export function readBulkChangeStamp(cell: string | null | undefined): BulkChangeKind | null {
  return readBulkChangeStampDetail(cell)?.kind ?? null;
}

/** The first column of every template: the record's id, empty for a new record. */
export const BULK_CHANGE_ID_KEY = 'id';

export const BULK_CHANGE_FAILURE_CODES = [
  'BULK_CHANGE_UNREADABLE',
  'BULK_CHANGE_FILE_ENCRYPTED',
  'BULK_CHANGE_TOO_MANY_ROWS',
  'BULK_CHANGE_TEMPLATE_UNRECOGNISED',
  'BULK_CHANGE_TEMPLATE_OTHER_KIND',
  'BULK_CHANGE_TEMPLATE_STALE',
  'BULK_CHANGE_COLUMN_UNKNOWN',
  'BULK_CHANGE_COLUMN_DUPLICATE',
  'BULK_CHANGE_COLUMN_MISSING',
  'BULK_CHANGE_EMPTY',
  'BULK_CHANGE_UPLOADER_REFUSED',
  'BULK_CHANGE_UPLOADER_MOVED',
  'BULK_CHANGE_FILE_INFECTED',
  'BULK_CHANGE_SCAN_FAILED',
  'BULK_CHANGE_FILE_CHANGED',
] as const;
export type BulkChangeFailureCode = (typeof BULK_CHANGE_FAILURE_CODES)[number];

export type BulkChangeFileRow = { rowNumber: number; cells: Record<string, string> };

export type BulkChangeFileRead =
  | { ok: true; rows: BulkChangeFileRow[]; stamp: BulkChangeStamp }
  | { ok: false; failure: BulkChangeFailureCode; column?: string };

export type BulkChangeFileInput = {
  readonly bytes: Uint8Array;
  readonly kind: BulkChangeFileKind;
  readonly inflateRaw: InflateRaw;
  readonly expectedKind: BulkChangeKind;
  /**
   * Every key a template of this kind carries — or, for a kind whose columns
   * depend on what A1 names (a donation's campaign), the keys of the template
   * that stamp names, or the failure that refuses the file.
   */
  readonly accepted:
    | readonly string[]
    | ((stamp: BulkChangeStamp) => Promise<readonly string[] | BulkChangeFailureCode>);
};

/** The grid is read with room for formatted empty rows below the data a template holds. */
const PHYSICAL_ROW_LIMIT = 2 + BULK_CHANGE_MAX_ROWS * 2;

function failureOf(error: unknown): BulkChangeFailureCode {
  const code =
    typeof error === 'object' && error !== null && 'code' in error
      ? String((error as { code: unknown }).code)
      : '';
  if (code === 'STATEMENT_FILE_ENCRYPTED') return 'BULK_CHANGE_FILE_ENCRYPTED';
  if (code === 'STATEMENT_TOO_MANY_ROWS') return 'BULK_CHANGE_TOO_MANY_ROWS';
  return 'BULK_CHANGE_UNREADABLE';
}

/**
 * An uploaded template read back — docs/decisions/0125 §3. A1 must be the stamp
 * of the kind the change was started for; row 2 is the header, matched by key
 * so a reordered sheet still reads and a column somebody added without a key is
 * ignored, while a key no template of this kind carries, a key twice or a
 * missing `id` column refuses the whole file. Data starts at row 3; an empty
 * row is skipped, and more than `BULK_CHANGE_MAX_ROWS` of them is refused
 * rather than truncated.
 */
export async function readBulkChangeFile(input: BulkChangeFileInput): Promise<BulkChangeFileRead> {
  let grid: string[][];
  try {
    grid = await readStatementGrid({
      kind: input.kind,
      bytes: input.bytes,
      decimalSeparator: ',',
      inflateRaw: input.inflateRaw,
      maxRows: PHYSICAL_ROW_LIMIT,
    });
  } catch (error) {
    return { ok: false, failure: failureOf(error) };
  }

  const stamped = readBulkChangeStampDetail(grid[0]?.[0]);
  if (stamped === null) return { ok: false, failure: 'BULK_CHANGE_TEMPLATE_UNRECOGNISED' };
  if (stamped.kind !== input.expectedKind) {
    return { ok: false, failure: 'BULK_CHANGE_TEMPLATE_OTHER_KIND' };
  }
  const keys =
    typeof input.accepted === 'function' ? await input.accepted(stamped) : input.accepted;
  if (typeof keys === 'string') return { ok: false, failure: keys };
  const accepted = new Set(keys);
  const keyAt = new Map<number, string>();
  const seen = new Set<string>();
  for (const [index, header] of (grid[1] ?? []).entries()) {
    const key = headerKeyOf(header);
    if (key === null) continue;
    if (!accepted.has(key))
      return { ok: false, failure: 'BULK_CHANGE_COLUMN_UNKNOWN', column: key };
    if (seen.has(key)) return { ok: false, failure: 'BULK_CHANGE_COLUMN_DUPLICATE', column: key };
    seen.add(key);
    keyAt.set(index, key);
  }
  if (!seen.has(BULK_CHANGE_ID_KEY)) {
    return { ok: false, failure: 'BULK_CHANGE_COLUMN_MISSING', column: BULK_CHANGE_ID_KEY };
  }

  const rows: BulkChangeFileRow[] = [];
  for (let index = 2; index < grid.length; index += 1) {
    const cells: Record<string, string> = {};
    let filled = false;
    for (const [column, text] of (grid[index] ?? []).entries()) {
      const key = keyAt.get(column);
      if (key === undefined) continue;
      cells[key] = text;
      if (text.trim() !== '') filled = true;
    }
    if (!filled) continue;
    if (rows.length >= BULK_CHANGE_MAX_ROWS) {
      return { ok: false, failure: 'BULK_CHANGE_TOO_MANY_ROWS' };
    }
    rows.push({ rowNumber: index + 1, cells });
  }
  if (rows.length === 0) return { ok: false, failure: 'BULK_CHANGE_EMPTY' };
  return { ok: true, rows, stamp: stamped };
}
